import {
  Check,
  ChevronsUpDown,
  LockKeyhole,
  Plus,
  Settings,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Input } from "./ui/input";
import { useToast } from "./ui/use-toast";

export function WorkspaceSwitcher() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    activeWorkspace,
    workspaces,
    switchWorkspace,
    createWorkspace,
    workspaceSwitching,
  } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const available = workspaces.filter(
    (workspace) => workspace.status === "active",
  );
  const unavailable = workspaces.filter(
    (workspace) => workspace.status === "suspended",
  );

  const submitCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;
    setError(null);
    try {
      await createWorkspace(name.trim());
      setName("");
      setCreateOpen(false);
      navigate("/generate");
    } catch (createError) {
      setError(
        createError instanceof Error
          ? createError.message
          : "Could not create workspace.",
      );
    }
  };

  const selectWorkspace = async (workspaceId: string) => {
    try {
      await switchWorkspace(workspaceId);
    } catch (switchError) {
      toast({
        title: "Could not switch workspace",
        description:
          switchError instanceof Error
            ? switchError.message
            : "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <div className="flex overflow-hidden rounded-lg border border-border bg-background">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2.5 text-left transition-colors hover:bg-muted focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            >
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                {activeWorkspace?.name || "Choose a workspace"}
              </span>
              <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-60">
            <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
              Workspaces
            </DropdownMenuLabel>
            {available.map((workspace) => (
              <DropdownMenuItem
                key={workspace.id}
                onSelect={() => void selectWorkspace(workspace.id)}
                disabled={workspaceSwitching}
                className="gap-2 py-2"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{workspace.name}</span>
                  <span className="block text-[11px] capitalize text-muted-foreground">
                    {workspace.role}
                  </span>
                </span>
                {workspace.id === activeWorkspace?.id && (
                  <Check className="h-4 w-4 shrink-0" />
                )}
              </DropdownMenuItem>
            ))}
            {unavailable.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
                  Unavailable
                </DropdownMenuLabel>
                {unavailable.map((workspace) => (
                  <DropdownMenuItem
                    key={workspace.id}
                    disabled
                    className="gap-2"
                  >
                    <LockKeyhole className="h-3.5 w-3.5 shrink-0" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{workspace.name}</span>
                      <span className="block text-[11px] capitalize text-muted-foreground">
                        {workspace.role}
                      </span>
                    </span>
                  </DropdownMenuItem>
                ))}
              </>
            )}
            <DropdownMenuSeparator />
            {activeWorkspace && (
              <DropdownMenuItem
                onSelect={() => navigate("/workspace")}
                className="gap-2"
              >
                <Settings className="h-4 w-4" />
                Workspace settings
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onSelect={() => setCreateOpen(true)}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Create workspace
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <form onSubmit={submitCreate}>
            <DialogHeader>
              <DialogTitle>Create a workspace</DialogTitle>
              <DialogDescription>
                Each workspace keeps its brand, connections, credits, and
                billing separate.
              </DialogDescription>
            </DialogHeader>
            <div className="py-5">
              <label
                htmlFor="workspace-name"
                className="mb-2 block text-sm font-medium"
              >
                Workspace name
              </label>
              <Input
                id="workspace-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Acme Studio"
                maxLength={60}
                autoFocus
              />
              {error && (
                <p className="mt-3 text-sm text-destructive">{error}</p>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!name.trim() || workspaceSwitching}
              >
                {workspaceSwitching ? "Creating…" : "Create workspace"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
